/**
 * Opmaak voor de klantkaart v6 (sessie 73): datums, duur, woorden.
 *
 * Alles in de tijdzone Europe/Amsterdam. Een ontbrekende of kapotte
 * waarde geeft `null` (of een lege tekst), nooit een verzonnen getal:
 * de schermcode toont dan `NogNiet`.
 */

const TZ = "Europe/Amsterdam";

function alsDatum(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "12 okt" */
export function datumKort(iso: string | null | undefined): string {
  const d = alsDatum(iso);
  if (!d) return "";
  // Een ander jaar dan nu: het jaar erbij ("12 okt 2024"), anders is de
  // datum niet te plaatsen.
  const ditJaar = new Date().toLocaleDateString("nl-NL", { timeZone: TZ, year: "numeric" });
  const zijnJaar = d.toLocaleDateString("nl-NL", { timeZone: TZ, year: "numeric" });
  return d
    .toLocaleDateString("nl-NL", {
      timeZone: TZ,
      day: "numeric",
      month: "short",
      ...(zijnJaar === ditJaar ? {} : { year: "numeric" }),
    })
    .replace(".", "");
}

/** "12 okt 2026" */
export function datumLang(iso: string | null | undefined): string {
  const d = alsDatum(iso);
  if (!d) return "";
  return d
    .toLocaleDateString("nl-NL", { timeZone: TZ, day: "numeric", month: "short", year: "numeric" })
    .replace(".", "");
}

/** "maart 2021" */
export function maandJaar(iso: string | null | undefined): string {
  const d = alsDatum(iso);
  if (!d) return "";
  return d.toLocaleDateString("nl-NL", { timeZone: TZ, month: "short", year: "numeric" }).replace(".", "");
}

/** "14:05" */
export function tijd(iso: string | null | undefined): string {
  const d = alsDatum(iso);
  if (!d) return "";
  return d.toLocaleTimeString("nl-NL", { timeZone: TZ, hour: "2-digit", minute: "2-digit" });
}

/** Dagsleutel "2026-10-12" in Amsterdam, voor groeperen per dag. */
export function dagSleutel(iso: string | null | undefined): string {
  const d = alsDatum(iso);
  if (!d) return "";
  return d.toLocaleDateString("sv-SE", { timeZone: TZ });
}

/** Kop van een dag in de tijdlijn: "Vandaag", "Gisteren" of "ma 12 okt 2026". */
export function dagKop(iso: string, nu: Date = new Date()): string {
  const sleutel = dagSleutel(iso);
  if (!sleutel) return "";
  const vandaag = dagSleutel(nu.toISOString());
  const gisteren = dagSleutel(new Date(nu.getTime() - 86_400_000).toISOString());
  if (sleutel === vandaag) return "Vandaag";
  if (sleutel === gisteren) return "Gisteren";
  const d = alsDatum(iso) as Date;
  return d
    .toLocaleDateString("nl-NL", {
      timeZone: TZ,
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    })
    .replace(/\./g, "");
}

/** Moment in de klantenlijst: vandaag de tijd, dit jaar "12 okt", anders met jaar. */
export function momentKort(iso: string | null | undefined, nu: Date = new Date()): string {
  const d = alsDatum(iso);
  if (!d) return "";
  if (dagSleutel(iso) === dagSleutel(nu.toISOString())) return tijd(iso);
  const jaarNu = nu.toLocaleDateString("nl-NL", { timeZone: TZ, year: "numeric" });
  const jaarD = d.toLocaleDateString("nl-NL", { timeZone: TZ, year: "numeric" });
  return jaarNu === jaarD ? datumKort(iso) : datumLang(iso);
}

/** Hele maanden tussen een datum en nu (afgerond naar beneden). */
function maandenSinds(iso: string, nu: Date): number | null {
  const d = alsDatum(iso);
  if (!d) return null;
  let maanden = (nu.getFullYear() - d.getFullYear()) * 12 + (nu.getMonth() - d.getMonth());
  if (nu.getDate() < d.getDate()) maanden -= 1;
  return Math.max(0, maanden);
}

/** Duur sinds een datum in woorden: "5 jaar", "8 maanden", "minder dan een maand". */
export function duurSinds(iso: string | null | undefined, nu: Date = new Date()): string {
  if (!iso) return "";
  const maanden = maandenSinds(iso, nu);
  if (maanden === null) return "";
  return leeftijdWoord(maanden) ?? "";
}

/** Leeftijd of duur in maanden als woord: "8 maanden", "1 jaar", "4 jaar". */
export function leeftijdWoord(maanden: number | null | undefined): string | null {
  if (maanden === null || maanden === undefined) return null;
  if (maanden < 1) return "minder dan een maand";
  if (maanden === 1) return "1 maand";
  if (maanden < 24) return `${maanden} maanden`;
  return `${Math.floor(maanden / 12)} jaar`;
}

/** Hele dagen van vandaag tot een datum (negatief: in het verleden). */
export function dagenTot(iso: string | null | undefined, nu: Date = new Date()): number | null {
  const sleutel = dagSleutel(iso);
  if (!sleutel) return null;
  const doel = Date.parse(`${sleutel}T00:00:00Z`);
  const vandaag = Date.parse(`${dagSleutel(nu.toISOString())}T00:00:00Z`);
  return Math.round((doel - vandaag) / 86_400_000);
}

/** De voornaam uit een volledige naam ("Dick van Leeuwen" wordt "Dick"). */
export function voornaamVan(naam: string): string {
  return naam.trim().split(/\s+/)[0] ?? naam;
}

/** Gram als leesbaar gewicht: 12000 wordt "12 kg", 800 wordt "800 g". */
export function gramWoord(gram: number | null | undefined): string | null {
  if (gram === null || gram === undefined) return null;
  if (gram >= 1000) {
    return `${(gram / 1000).toLocaleString("nl-NL", { maximumFractionDigits: 1 })} kg`;
  }
  return `${Math.round(gram)} g`;
}

export function getal(n: number, decimalen = 0): string {
  return n.toLocaleString("nl-NL", { maximumFractionDigits: decimalen });
}

/** Het woord bij een aantal: "1 bestelling", "3 bestellingen". */
export function aantalWoord(n: number, enkel: string, meer: string): string {
  return `${getal(n)} ${n === 1 ? enkel : meer}`;
}

/** Is dit een mobiel nummer (NL 06, BE 04)? Voor de WhatsApp-knop. */
export function isMobiel(nummer: string): boolean {
  const schoon = nummer.replace(/[\s\-().]/g, "");
  return /^(\+316|00316|06|\+324|00324|04)\d{7,8}$/.test(schoon);
}

/** Diersoort en aantal: "1 hond", "2 katten", "2 dieren". */
export function dierenKop(soorten: string[]): string {
  if (soorten.length === 0) return "Dier";
  const uniek = new Set(soorten.map((s) => s.toLowerCase()));
  if (uniek.size === 1) {
    const soort = [...uniek][0];
    if (soorten.length === 1) return `1 ${soort}`;
    const meervoud: Record<string, string> = { hond: "honden", kat: "katten", konijn: "konijnen" };
    return `${soorten.length} ${meervoud[soort] ?? "dieren"}`;
  }
  return `${soorten.length} dieren`;
}

/** Geslacht als woord bij de diersoort: reu of teef, kater of poes. */
export function geslachtWoord(diersoort: string, geslacht: string | null): string | null {
  if (!geslacht) return null;
  const g = geslacht.trim().toLowerCase();
  const man = ["m", "man", "mannelijk", "male", "reu", "kater"].includes(g);
  const vrouw = ["v", "vrouw", "vrouwelijk", "female", "teef", "poes"].includes(g);
  const soort = diersoort.toLowerCase();
  if (man) return soort === "kat" ? "kater" : soort === "hond" ? "reu" : "mannetje";
  if (vrouw) return soort === "kat" ? "poes" : soort === "hond" ? "teef" : "vrouwtje";
  return geslacht;
}

/** Castratie als woord; `null` is onbekend. */
export function castratieWoord(castratie: boolean | null, geslacht: string | null): string | null {
  if (castratie === null) return null;
  const vrouw = geslacht ? ["v", "vrouw", "vrouwelijk", "female", "teef", "poes"].includes(geslacht.toLowerCase()) : false;
  if (castratie) return vrouw ? "gesteriliseerd" : "gecastreerd";
  return vrouw ? "niet gesteriliseerd" : "niet gecastreerd";
}
